/*
 * <vehicle-ymm> — Year → Make → Model selector.
 * Data: a JSON script inside the element, built from the "vehicles" menu:
 *   { makes: [{ t, u, v: [{ t: "M3/M4 G80/G82/G83 (2021+)", u: "/collections/..." }] }] }
 * Years come from the "(2021+)" / "(2014-2021)" suffix of each vehicle title.
 */
const STORE_KEY = 'epc-vehicle';
const YEAR_RX = /\((\d{4})\s*(?:-\s*(\d{4})|(\+))?\)\s*$/;

class VehicleYmm extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    const dataEl = this.querySelector('[data-ymm-data]');
    let data = { makes: [] };
    try { data = JSON.parse(dataEl ? dataEl.textContent : '{}'); } catch (e) { /* keep empty */ }

    const maxYear = new Date().getFullYear() + 1;
    this.makes = (data.makes || []).map((m) => ({
      title: m.t,
      url: m.u,
      vehicles: (m.v || []).map((v, i) => {
        const match = v.t.match(YEAR_RX);
        const from = match ? parseInt(match[1], 10) : 0;
        const to = match ? (match[2] ? parseInt(match[2], 10) : match[3] ? maxYear : from) : 0;
        return { label: v.t.replace(YEAR_RX, '').trim(), title: v.t, url: v.u, from, to, order: i };
      }).filter((v) => v.from && v.url),
    })).filter((m) => m.vehicles.length);

    this.yearSel = this.querySelector('[data-ymm-year]');
    this.makeSel = this.querySelector('[data-ymm-make]');
    this.modelSel = this.querySelector('[data-ymm-model]');
    this.button = this.querySelector('[data-ymm-go]');
    this.clearBtn = this.querySelector('[data-ymm-clear]');
    this.form = this.querySelector('form');
    if (!this.yearSel || !this.makeSel || !this.modelSel || !this.form) return;

    this.fillYears();
    this.yearSel.addEventListener('change', () => this.onYear());
    this.makeSel.addEventListener('change', () => this.onMake());
    this.modelSel.addEventListener('change', () => this.update());
    this.form.addEventListener('submit', (e) => this.onSubmit(e));
    if (this.clearBtn) this.clearBtn.addEventListener('click', () => this.clearSelection());
    this.restore();
  }

  toggleClear() {
    if (this.clearBtn) this.clearBtn.hidden = !this.yearSel.value;
  }

  clearSelection() {
    try { localStorage.removeItem(STORE_KEY); } catch (err) { /* storage unavailable */ }
    this.fillYears();
    this.toggleClear();
  }

  option(value, text) {
    const o = document.createElement('option');
    o.value = value;
    o.textContent = text;
    return o;
  }

  reset(select, placeholder, disabled) {
    select.replaceChildren(this.option('', placeholder));
    select.disabled = disabled;
  }

  fits(v, year) { return year >= v.from && year <= v.to; }

  fillYears() {
    const years = new Set();
    this.makes.forEach((m) => m.vehicles.forEach((v) => {
      for (let y = v.from; y <= v.to; y++) years.add(y);
    }));
    this.reset(this.yearSel, this.yearSel.dataset.placeholder || 'Year', false);
    [...years].sort((a, b) => b - a).forEach((y) => this.yearSel.appendChild(this.option(y, y)));
    this.reset(this.makeSel, this.makeSel.dataset.placeholder || 'Make', true);
    this.reset(this.modelSel, this.modelSel.dataset.placeholder || 'Model', true);
    this.update();
  }

  onYear() {
    const year = parseInt(this.yearSel.value, 10);
    this.reset(this.makeSel, this.makeSel.dataset.placeholder || 'Make', !year);
    this.reset(this.modelSel, this.modelSel.dataset.placeholder || 'Model', true);
    if (year) {
      this.makes.forEach((m, i) => {
        if (m.vehicles.some((v) => this.fits(v, year))) this.makeSel.appendChild(this.option(i, m.title));
      });
    }
    this.update();
    this.toggleClear();
  }

  onMake() {
    const year = parseInt(this.yearSel.value, 10);
    const make = this.makes[this.makeSel.value];
    this.reset(this.modelSel, this.modelSel.dataset.placeholder || 'Model', !make);
    if (make) {
      make.vehicles.filter((v) => this.fits(v, year)).forEach((v) => {
        this.modelSel.appendChild(this.option(v.order, v.label));
      });
    }
    this.update();
  }

  selection() {
    const make = this.makes[this.makeSel.value];
    const vehicle = make && this.modelSel.value !== '' ? make.vehicles.find((v) => String(v.order) === this.modelSel.value) : null;
    return { year: this.yearSel.value, make, vehicle };
  }

  update() {
    const { make } = this.selection();
    if (this.button) this.button.disabled = !make;
  }

  onSubmit(e) {
    e.preventDefault();
    const { year, make, vehicle } = this.selection();
    if (!make) return;
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify({ year, make: make.title, url: vehicle ? vehicle.url : '' }));
    } catch (err) { /* storage unavailable */ }
    window.location.href = vehicle ? vehicle.url : make.url;
  }

  restore() {
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(STORE_KEY) || 'null'); } catch (err) { saved = null; }
    if (!saved || !saved.year) return;
    if (![...this.yearSel.options].some((o) => o.value === String(saved.year))) return;
    this.yearSel.value = String(saved.year);
    this.onYear();
    const makeIndex = this.makes.findIndex((m) => m.title === saved.make);
    if (makeIndex < 0 || ![...this.makeSel.options].some((o) => o.value === String(makeIndex))) return;
    this.makeSel.value = String(makeIndex);
    this.onMake();
    const vehicle = this.makes[makeIndex].vehicles.find((v) => v.url === saved.url);
    if (vehicle && [...this.modelSel.options].some((o) => o.value === String(vehicle.order))) {
      this.modelSel.value = String(vehicle.order);
    }
    this.update();
    this.toggleClear();
  }
}

if (!customElements.get('vehicle-ymm')) customElements.define('vehicle-ymm', VehicleYmm);
